import { useMemo, useState } from 'react';
import type { Dayjs } from 'dayjs';
import { uniqueLabels } from '@evimo/types/constants/session/filter-charging-hours';
import { useDebouncedValue } from '~/app/hooks/useDebouncedValue';
import {
  useSessionAssetComponents,
  useSessionAssets,
  useSessionCharges,
  useSessionSites,
} from '~/app/components/session/hooks/useSessionQueries';

export function useChargesTable() {
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [siteName, setSiteName] = useState<string>();
  const [chargerName, setChargerName] = useState<string>();
  const [connectorName, setConnectorName] = useState<string>();
  const [sessionStatus, setSessionStatus] = useState<string>();
  const [anomalyQualification, setAnomalyQualification] = useState<string>();
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [siteSearch, setSiteSearch] = useState('');
  const [chargerSearch, setChargerSearch] = useState('');
  const [connectorSearch, setConnectorSearch] = useState('');

  const debouncedSiteSearch = useDebouncedValue(siteSearch, 300);
  const debouncedChargerSearch = useDebouncedValue(chargerSearch, 300);
  const debouncedConnectorSearch = useDebouncedValue(connectorSearch, 300);

  const from = range ? range[0].startOf('day').toISOString() : undefined;
  const to = range ? range[1].endOf('day').toISOString() : undefined;

  const query = useSessionCharges({
    page,
    limit: pageSize,
    q: search || undefined,
    siteName,
    chargerName,
    connectorName,
    sessionStatus,
    anomalyQualification,
    from,
    to,
  });

  const sitesQuery = useSessionSites(debouncedSiteSearch);
  const assetsQuery = useSessionAssets(debouncedChargerSearch);

  const assetIdByName = useMemo(() => {
    const map = new Map<string, string>();
    for (const asset of assetsQuery.data?.data ?? []) {
      if (!map.has(asset.assetName)) {
        map.set(asset.assetName, asset.id);
      }
    }
    return map;
  }, [assetsQuery.data]);

  const selectedAssetId = chargerName
    ? (assetIdByName.get(chargerName) ?? null)
    : null;
  const componentsQuery = useSessionAssetComponents(
    selectedAssetId,
    debouncedConnectorSearch,
  );

  const charges = query.data?.data ?? [];
  const total = query.data?.meta.total ?? 0;

  const sites = useMemo(() => {
    const values = (sitesQuery.data?.data ?? []).map((s) => s.name);
    if (siteName && !values.includes(siteName)) {
      values.push(siteName);
    }
    return uniqueLabels(values);
  }, [sitesQuery.data, siteName]);

  const chargers = useMemo(() => {
    const values = (assetsQuery.data?.data ?? []).map((a) => a.assetName);
    if (chargerName && !values.includes(chargerName)) {
      values.push(chargerName);
    }
    return uniqueLabels(values);
  }, [assetsQuery.data, chargerName]);

  const connectors = useMemo(() => {
    const values = (componentsQuery.data?.data ?? []).map((c) => c.name);
    if (connectorName && !values.includes(connectorName)) {
      values.push(connectorName);
    }
    return uniqueLabels(values);
  }, [componentsQuery.data, connectorName]);

  return {
    range,
    siteName,
    chargerName,
    connectorName,
    sessionStatus,
    anomalyQualification,
    search,
    page,
    pageSize,
    sites,
    chargers,
    connectors,
    charges,
    total,
    isLoading: query.isLoading,
    isError: query.isError,
    setRange: (value: [Dayjs, Dayjs] | null) => {
      setRange(value);
      setPage(1);
    },
    setSiteName: (value?: string) => {
      setSiteName(value);
      setChargerName(undefined);
      setConnectorName(undefined);
      setPage(1);
    },
    setChargerName: (value?: string) => {
      setChargerName(value);
      setConnectorName(undefined);
      setPage(1);
    },
    setConnectorName: (value?: string) => {
      setConnectorName(value);
      setPage(1);
    },
    setSessionStatus: (value?: string) => {
      setSessionStatus(value);
      setPage(1);
    },
    setAnomalyQualification: (value?: string) => {
      setAnomalyQualification(value);
      setPage(1);
    },
    setSearch: (value: string) => {
      setSearch(value);
      setPage(1);
    },
    onSiteSearch: setSiteSearch,
    onChargerSearch: setChargerSearch,
    onConnectorSearch: setConnectorSearch,
    setPage,
    setPageSize: (size: number) => {
      setPageSize(size);
      setPage(1);
    },
  };
}
